import type { TourId } from './types';

const STORAGE_KEY = 'markly_tours';

interface TourStorage {
  completed: TourId[];
  skipped: TourId[];
  lastShown: Record<TourId, number>;
}

const defaultStorage: TourStorage = {
  completed: [],
  skipped: [],
  lastShown: {} as Record<TourId, number>,
};

/**
 * Get tour storage from localStorage
 */
export function getTourStorage(): TourStorage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultStorage, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to parse tour storage:', e);
  }
  return defaultStorage;
}

/**
 * Save tour storage to localStorage
 */
export function saveTourStorage(storage: TourStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch (e) {
    console.error('Failed to save tour storage:', e);
  }
}

/**
 * Mark a tour as completed
 */
export function markTourCompleted(tourId: TourId): void {
  const storage = getTourStorage();
  if (!storage.completed.includes(tourId)) {
    storage.completed.push(tourId);
  }
  // Remove from skipped if it was there
  storage.skipped = storage.skipped.filter((id) => id !== tourId);
  storage.lastShown[tourId] = Date.now();
  saveTourStorage(storage);
}

/**
 * Mark a tour as skipped
 */
export function markTourSkipped(tourId: TourId): void {
  const storage = getTourStorage();
  if (!storage.skipped.includes(tourId)) {
    storage.skipped.push(tourId);
  }
  storage.lastShown[tourId] = Date.now();
  saveTourStorage(storage);
}

/**
 * Check if a tour is completed
 */
export function isTourCompleted(tourId: TourId): boolean {
  const storage = getTourStorage();
  return storage.completed.includes(tourId);
}

/**
 * Check if a tour is skipped
 */
export function isTourSkipped(tourId: TourId): boolean {
  const storage = getTourStorage();
  return storage.skipped.includes(tourId);
}

/**
 * Reset a specific tour
 */
export function resetTour(tourId: TourId): void {
  const storage = getTourStorage();
  storage.completed = storage.completed.filter((id) => id !== tourId);
  storage.skipped = storage.skipped.filter((id) => id !== tourId);
  delete storage.lastShown[tourId];
  saveTourStorage(storage);
}

/**
 * Reset all tours
 */
export function resetAllTours(): void {
  saveTourStorage(defaultStorage);
}

/**
 * Get completed tours
 */
export function getCompletedTours(): TourId[] {
  return getTourStorage().completed;
}

/**
 * Get skipped tours
 */
export function getSkippedTours(): TourId[] {
  return getTourStorage().skipped;
}
